import { Partner } from '@hub-services/partner';
import { Subfleet } from '@hub-services/subfleets.service';
import { User } from '@hub-services/user.service';
import { createReducer, on } from '@ngrx/store';
import * as PartnerActions from './selected-partner.actions';

export const featureKey = 'selectedPartner';

// Collections seed empty; `partner` is a single object and stays nullable. This matches NgRx
// guidance: don't overload null-vs-empty on collection state. If any UX needs to distinguish
// "loading" from "loaded and empty" for a list, add an explicit isLoading/isLoaded flag rather
// than making the collection itself nullable.
export interface HubPartnerState {
  partner: Partner | null;
  subfleets: Subfleet[];
  users: User[];
  admins: User[];
}

export const initialState: HubPartnerState = {
  partner: null,
  subfleets: [],
  users: [],
  admins: [],
};

export const reducer = createReducer(
  initialState,

  on(PartnerActions.setPartner, (state, action) => ({
    ...state,
    partner: action.partner,
  })),
  on(PartnerActions.setUsersAndAdmins, (state, action) => ({
    ...state,
    users: action.users,
    admins: action.admins,
  })),
  on(PartnerActions.setSubfeeets, (state, action) => ({
    ...state,
    subfleets: action.subfleets,
  })),
  on(PartnerActions.createUserInStore, (state, action) => ({
    ...state,
    users: [...state.users, action.user],
  })),
  on(PartnerActions.updateUserInStore, (state, action) => {
    // No-op if the user isn't in the list — findIndex returning -1 and being used as an index
    // would silently attach a `-1` property to the array copy without touching indexed entries.
    const userIdx = state.users.findIndex(user => user.id === action.user.id);
    if (userIdx === -1) return state;

    const users = [...state.users];
    users[userIdx] = { ...action.user };

    return {
      ...state,
      users,
    };
  }),
  on(PartnerActions.deleteUserFromStore, (state, action) => ({
    ...state,
    users: state.users.filter(user => user.id !== action.id),
  })),

  on(PartnerActions.addCategoryToStore, (state, action) => {
    // Category add is a no-op until a partner is loaded (partner is a scalar, stays nullable).
    if (!state.partner) return state;
    return {
      ...state,
      partner: {
        ...state.partner,
        categories: [
          ...(state.partner.categories ?? []),
          { id: action.id, name: action.name },
        ]
      },
    };
  }),

  on(PartnerActions.setPartnerBrandsInStore, (state, action) => {
    if (!state.partner) return state;
    return {
      ...state,
      partner: {
        ...state.partner,
        brands: [
          ...action.brands,
        ]
      },
    };
  }),

  on(PartnerActions.resetPartnerState, state => ({
    ...state,
    ...initialState,
  })),

);

